import React, { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import DrivePreview from './DrivePreview';

/**
 * FileViewerModal — previews task attachments inline.
 *
 * Backend attachment endpoint requires an Authorization header, which browsers
 * cannot attach to <img src>, <audio src>, <iframe src>, etc. To work around
 * this we fetch the file with auth, build an in-memory object URL, and feed
 * that into the native viewer element. The object URL is revoked on cleanup
 * to avoid memory leaks.
 */
const FileViewerModal = ({ isOpen, onClose, file }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const revokeRef = useRef(null);

  const contentType = (file?.content_type || file?.type || '').toLowerCase();

  // Build the underlying source: either an authenticated backend URL we need to
  // fetch, or an already-usable data/file URL we can pass straight through.
  const directUrl = (() => {
    if (!file) return null;
    if (file.content) return `data:${contentType};base64,${file.content}`;
    if (file.file_url) return file.file_url;
    if (file.data_url) return file.data_url;
    return null;
  })();

  const isDrive = file?.provider === 'google';
  const needsAuthFetch = !!(!isDrive && file?.id && file?.task_id && !directUrl);
  const authUrl = needsAuthFetch
    ? `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${file.task_id}/attachments/${file.id}`
    : null;

  // Fetch the file (with auth) and turn the response into an object URL.
  useEffect(() => {
    if (!isOpen || !file) return undefined;

    // Reset prior state on each open / file change.
    setObjectUrl(null);
    setTextContent(null);
    setErrorMsg('');
    if (isDrive) return undefined;

    // If we already have a usable URL (data: or external), just use it.
    if (directUrl) {
      setObjectUrl(directUrl);
      setStatus('ready');
      return undefined;
    }

    if (!authUrl) {
      setStatus('error');
      setErrorMsg('No source available for this file.');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    (async () => {
      try {
        const resp = await fetch(authUrl, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (!resp.ok) {
          throw new Error(`Server responded ${resp.status}`);
        }
        const blob = await resp.blob();
        if (cancelled) return;

        // Text files: also read as text so we can render inline preview.
        if (contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('csv')) {
          try {
            const txt = await blob.text();
            if (!cancelled) setTextContent(txt.slice(0, 20000)); // cap to 20k chars
          } catch (_) { /* ignore, fall back to download */ }
        }

        const url = URL.createObjectURL(blob);
        revokeRef.current = url;
        setObjectUrl(url);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        console.error('FileViewer fetch failed:', err);
        setStatus('error');
        setErrorMsg(err.message || 'Failed to load preview');
      }
    })();

    return () => {
      cancelled = true;
      if (revokeRef.current) {
        URL.revokeObjectURL(revokeRef.current);
        revokeRef.current = null;
      }
    };
  }, [isOpen, file, isDrive, directUrl, authUrl, contentType]);

  if (!isOpen || !file) return null;

  if (isDrive) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={file.filename || 'file'} size="lg">
        <DrivePreview file={file} onClose={onClose} />
      </Modal>
    );
  }

  const filename = file.filename || file.name || 'file';

  const handleDownload = () => {
    if (!objectUrl) return;
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderPreview = () => {
    if (status === 'loading') {
      return (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
          <p className="text-sm text-gray-600">Loading preview…</p>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="flex flex-col items-center justify-center h-64 bg-red-50 rounded-lg">
          <div className="text-4xl mb-2">⚠️</div>
          <p className="text-red-700 font-medium">Could not load preview</p>
          <p className="text-xs text-red-600 mt-1">{errorMsg}</p>
        </div>
      );
    }

    if (status !== 'ready' || !objectUrl) return null;

    // Images
    if (contentType.startsWith('image/')) {
      return (
        <div className="flex justify-center bg-gray-50 rounded-lg p-2">
          <img
            src={objectUrl}
            alt={filename}
            className="max-w-full max-h-[70vh] rounded-md object-contain"
          />
        </div>
      );
    }

    // Audio
    if (contentType.startsWith('audio/')) {
      return (
        <div className="text-center">
          <div className="flex items-center justify-center h-32 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg mb-4">
            <div className="text-white text-center">
              <div className="text-4xl mb-2">🎵</div>
              <p className="font-medium truncate max-w-xs">{filename}</p>
            </div>
          </div>
          <audio controls className="w-full max-w-md mx-auto" src={objectUrl}>
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    // Video
    if (contentType.startsWith('video/')) {
      return (
        <video
          controls
          className="max-w-full max-h-[70vh] mx-auto rounded-lg shadow"
          src={objectUrl}
        >
          Your browser does not support the video element.
        </video>
      );
    }

    // PDF
    if (contentType.includes('pdf')) {
      return (
        <iframe
          src={objectUrl}
          className="w-full h-[70vh] border rounded-lg bg-white"
          title={filename}
        />
      );
    }

    // Text / JSON / CSV — show inline content if we captured it
    if (textContent != null) {
      return (
        <pre className="text-left bg-gray-50 border border-gray-200 p-4 rounded-lg max-h-[60vh] overflow-auto text-xs whitespace-pre-wrap break-words text-gray-800">
          {textContent}
        </pre>
      );
    }

    // Office docs (.docx/.xlsx/.pptx) and unknown — no native browser preview.
    // We can't proxy through Office/Google viewers because the backend URL is
    // private. Best UX is a clear "download to open" prompt.
    const isOffice = /(word|sheet|presentation|excel|powerpoint|officedocument)/.test(contentType);
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg text-center px-6">
        <div className="text-5xl mb-3">{isOffice ? '📄' : '📎'}</div>
        <p className="font-medium text-gray-900 truncate max-w-full">{filename}</p>
        <p className="text-sm text-gray-600 mt-1">{contentType || 'Unknown type'}</p>
        <p className="text-xs text-gray-500 mt-3 max-w-sm">
          Inline preview isn’t available for this file type. Use Download to open it in the matching app.
        </p>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={filename}
      size="lg"
    >
      <div className="p-6">
        <div className="mb-6" data-testid="file-viewer-content">
          {renderPreview()}
        </div>

        {/* File Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">File Information</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium truncate ml-2 max-w-[60%] text-right">{filename}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium">{file.content_type || file.type || 'Unknown'}</span>
            </div>
            {(file.size_mb || file.size) && (
              <div className="flex justify-between">
                <span className="text-gray-600">Size:</span>
                <span className="font-medium">
                  {file.size_mb ? `${file.size_mb} MB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            fullWidth
            onClick={handleDownload}
            disabled={!objectUrl}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="file-viewer-download"
          >
            Download
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FileViewerModal;
